import { useNavigate } from "react-router-dom";
import { t } from "ttag";

import { ROUTES } from "@/app/router";
import type { Subscription } from "@/features/billing/models";
import { isInactiveSubscription } from "@/features/consultations/utils";
import { SUBSCRIPTION_STATUS } from "@/shared/domains";
import { Alert, Button } from "@/shared/ui";

interface InactiveSubscriptionAlertProps {
  subscriptionStatus: Subscription["status"];
}

export const InactiveSubscriptionAlert = ({
  subscriptionStatus,
}: InactiveSubscriptionAlertProps) => {
  const navigate = useNavigate();

  if (!isInactiveSubscription(subscriptionStatus)) {
    return null;
  }

  const isPaymentFailed =
    subscriptionStatus === SUBSCRIPTION_STATUS.PAYMENT_FAILED ||
    subscriptionStatus === SUBSCRIPTION_STATUS.INCOMPLETE;

  const handleClick = () => {
    const route = isPaymentFailed
      ? ROUTES.BILLING.OVERVIEW
      : ROUTES.SHOP.OVERVIEW;

    return navigate(route);
  };

  return (
    <Alert type="warning" className="border-yellow-500">
      <div className="flex flex-col gap-4 lg:flex-row lg:gap-2">
        <div className="flex grow flex-col gap-1">
          <h6 className="font-semibold text-yellow-950">
            {isPaymentFailed
              ? t`Payment failed!`
              : t`Oops! Your subscription is on pause`}
          </h6>
          <p>
            {isPaymentFailed
              ? t`There was an issue processing your payment. Please check your details and try again, or use a different payment method.`
              : t`Looks like you've canceled your subscription, so your account is on hold for now. You won't receive any medication or be charged until you reactivate it.`}
          </p>
        </div>

        <Button
          onClick={handleClick}
          size="lg"
          className="-ml-6 sm:ml-0 sm:self-start lg:self-center"
        >
          {isPaymentFailed ? t`Update payment` : t`Go to Shop`}
        </Button>
      </div>
    </Alert>
  );
};
